import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Linkedin, Mail, Github, ArrowLeft, Code, Smartphone, Zap, Braces, GitBranch, Users, Globe, GraduationCap } from 'lucide-react';
import cemImage from '../assets/team/cem.png';

// ------------------
// Styled Components
// ------------------
const PageContainer = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  min-height: 100vh;
  padding-top: 20px;
`;

const MemberContainer = styled.div`
  padding: 30px 50px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 20px 20px;
  }
`;

const Navigation = styled(motion.nav)`
  margin-bottom: 50px;
`;

const BackLink = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: ${({ theme }) => theme.borderRadius.round};
  background: rgba(30, 30, 30, 0.5);
  color: ${({ theme }) => theme.colors.text.secondary};
  box-shadow: ${({ theme }) => theme.shadows.small};
  transition: all ${({ theme }) => theme.transitions.fast};
  cursor: pointer;
  
  &:hover {
    color: white;
    background: ${({ theme }) => theme.colors.accentColor.primary};
    transform: translateX(-5px);
    box-shadow: ${({ theme }) => theme.shadows.accent};
  }
`;

const ProfileSection = styled(motion.section)`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 50px;
  margin-bottom: 70px;
  align-items: start;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ProfileImageContainer = styled(motion.div)`
  border-radius: ${({ theme }) => theme.borderRadius.large};
  overflow: hidden;
  position: relative;
  box-shadow: ${({ theme }) => theme.shadows.medium};
  background: #ffffff;
  width: fit-content;
  align-self: flex-start; /* Keep image at the top */
`;

const ProfileImage = styled.img`
  max-width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.large};
  display: block;
`;

const ProfileInfo = styled(motion.div)`
  display: flex;
  flex-direction: column;
  text-align: left;
`;

const ProfileName = styled(motion.h1)`
  font-size: clamp(2.2rem, 4vw, 3rem);
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin-bottom: 10px;
  background: ${({ theme }) => theme.colors.gradient.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const ProfileTitle = styled(motion.h2)`
  font-size: 1.2rem;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 25px;
`;

const ProfileBio = styled(motion.p)`
  font-size: 1.05rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 30px;
`;

const SectionTitle = styled(motion.h3)`
  font-size: 1.4rem;
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 20px;
  position: relative;
  padding-left: 15px;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 4px;
    background: ${({ theme }) => theme.colors.gradient.primary};
    border-radius: ${({ theme }) => theme.borderRadius.small};
  }
`;

const ExpertiseSection = styled(motion.div)`
  background: rgba(30, 30, 30, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: 30px;
  margin-bottom: 40px;
  box-shadow: ${({ theme }) => theme.shadows.small};
`;

const ExpertiseContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ExpertiseText = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 1rem;
  line-height: 1.7;
`;

const SkillsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 10px;
`;

const SkillItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(255, 91, 58, 0.10);
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 0.9rem;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const SkillIcon = styled.div`
  color: ${({ theme }) => theme.colors.accentColor.primary};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProjectsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
`;

const ProjectItem = styled(motion.div)`
  background: rgba(255, 91, 58, 0.05);
  border: 1px solid rgba(255, 91, 58, 0.10);
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: 8px 16px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 0.9rem;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const EducationItem = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
`;

const EducationIcon = styled.div`
  color: ${({ theme }) => theme.colors.accentColor.primary};
  background: rgba(255, 91, 58, 0.10);
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.round};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const EducationContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const EducationDegree = styled.div`
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 5px;
`;

const EducationSchool = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ContactSection = styled(ExpertiseSection)`
  margin-bottom: 0;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 20px;
`;

const SocialIcon = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  border-radius: ${({ theme }) => theme.borderRadius.round};
  background: rgba(255, 255, 255, 0.05);
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all ${({ theme }) => theme.transitions.medium};
  
  &:hover {
    background: ${({ theme }) => theme.colors.accentColor.primary};
    color: white;
    transform: translateY(-3px);
    box-shadow: ${({ theme }) => theme.shadows.accent};
  }
`;

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const skillVariant = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: { type: 'spring', stiffness: 300, damping: 24 }
  }
};

// ------------------
// Main Component
// ------------------
const CemSertkaya = () => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    window.scrollTo(0, 0);
    // Slight delay to ensure animations trigger after navigation
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <PageContainer>
      <MemberContainer>
        {/* Navigation */}
        <Navigation
          initial={{ opacity: 0, x: -20 }}
          animate={isVisible ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.5 }}
        >
          <Link to="/" style={{ textDecoration: 'none' }}>
            <BackLink
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ArrowLeft size={24} />
            </BackLink>
          </Link>
        </Navigation>

        {/* Profile Section */}
        <ProfileSection
          initial={{ opacity: 0 }}
          animate={isVisible ? { opacity: 1 } : {}}
          transition={{ duration: 0.5 }}
        >
          <ProfileImageContainer
            initial={{ opacity: 0, x: -30 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <ProfileImage src={cemImage} alt="Cem Sertkaya" loading="eager" decoding="async" fetchpriority="high" />
          </ProfileImageContainer>

          <ProfileInfo>
            <ProfileName
              initial={{ opacity: 0, y: -20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              Cem Sertkaya
            </ProfileName>
            
            <ProfileTitle
              initial={{ opacity: 0, y: -20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Senior iOS Engineer &amp; Co-Founder
            </ProfileTitle>
            
            <ProfileBio
              initial={{ opacity: 0, y: 20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              Driven by a passion for building world-class mobile experiences, I specialize 
              in creating robust, user-centric iOS applications. In parallel with my 
              technical pursuits, I co-founded a growing consultancy, where I guide businesses 
              in architecting sustainable and innovative software solutions.
            </ProfileBio>

            {/* Expertise Section */}
            <ExpertiseSection
              variants={fadeInUp}
              initial="hidden"
              animate={isVisible ? "visible" : "hidden"}
              transition={{ delay: 0.6 }}
            >
              <SectionTitle>Professional Achievements</SectionTitle>
              <ExpertiseContent>
                <ExpertiseText>
                  My career began at Vestel A.S.—one of Turkey's premier technology companies—
                  where I honed my skills as an iOS developer during my undergraduate studies. 
                  Building on that foundation, I progressed to INVIO, contributing to the 
                  architecture and development of flagship Turkish mobile applications.
                </ExpertiseText>
                <SkillsList>
                  <SkillItem 
                    variants={skillVariant}
                    initial="hidden"
                    animate={isVisible ? "visible" : "hidden"}
                    transition={{ delay: 0.7 }}
                  >
                    <SkillIcon><Code size={16} /></SkillIcon>
                    Senior iOS Engineer
                  </SkillItem>
                  <SkillItem 
                    variants={skillVariant}
                    initial="hidden"
                    animate={isVisible ? "visible" : "hidden"}
                    transition={{ delay: 0.8 }}
                  >
                    <SkillIcon><Smartphone size={16} /></SkillIcon>
                    Mobile Application Expert
                  </SkillItem>
                  <SkillItem 
                    variants={skillVariant}
                    initial="hidden"
                    animate={isVisible ? "visible" : "hidden"}
                    transition={{ delay: 0.9 }}
                  >
                    <SkillIcon><Braces size={16} /></SkillIcon>
                    iOS Architecture Specialist
                  </SkillItem>
                </SkillsList>
              </ExpertiseContent>
            </ExpertiseSection>

            {/* Technical Expertise */}
            <ExpertiseSection
              variants={fadeInUp}
              initial="hidden"
              animate={isVisible ? "visible" : "hidden"}
              transition={{ delay: 0.7 }}
            >
              <SectionTitle>Technical Mastery</SectionTitle>
              <SkillsList>
                <SkillItem 
                  variants={skillVariant}
                  initial="hidden"
                  animate={isVisible ? "visible" : "hidden"}
                >
                  <SkillIcon><Smartphone size={16} /></SkillIcon>
                  iOS Development
                </SkillItem>
                <SkillItem 
                  variants={skillVariant}
                  initial="hidden"
                  animate={isVisible ? "visible" : "hidden"}
                >
                  <SkillIcon><Code size={16} /></SkillIcon>
                  Swift & SwiftUI
                </SkillItem>
                <SkillItem 
                  variants={skillVariant}
                  initial="hidden"
                  animate={isVisible ? "visible" : "hidden"}
                >
                  <SkillIcon><Zap size={16} /></SkillIcon>
                  UIKit
                </SkillItem>
                <SkillItem 
                  variants={skillVariant}
                  initial="hidden"
                  animate={isVisible ? "visible" : "hidden"}
                >
                  <SkillIcon><Globe size={16} /></SkillIcon>
                  AR/VR Development
                </SkillItem>
                <SkillItem 
                  variants={skillVariant}
                  initial="hidden"
                  animate={isVisible ? "visible" : "hidden"}
                >
                  <SkillIcon><Globe size={16} /></SkillIcon>
                  Generative AI & LLM Integration
                </SkillItem>
                <SkillItem 
                  variants={skillVariant}
                  initial="hidden"
                  animate={isVisible ? "visible" : "hidden"}
                >
                  <SkillIcon><Braces size={16} /></SkillIcon>
                  Mobile Architecture
                </SkillItem>
                <SkillItem 
                  variants={skillVariant}
                  initial="hidden"
                  animate={isVisible ? "visible" : "hidden"}
                >
                  <SkillIcon><GitBranch size={16} /></SkillIcon>
                  Version Control
                </SkillItem>
                <SkillItem 
                  variants={skillVariant}
                  initial="hidden"
                  animate={isVisible ? "visible" : "hidden"}
                >
                  <SkillIcon><Users size={16} /></SkillIcon>
                  Agile Development
                </SkillItem>
              </SkillsList>
            </ExpertiseSection>

            {/* Professional Milestones */}
            <ExpertiseSection
              variants={fadeInUp}
              initial="hidden"
              animate={isVisible ? "visible" : "hidden"}
              transition={{ delay: 0.8 }}
            >
              <SectionTitle>Notable Projects</SectionTitle>
              <ExpertiseContent>
                <ExpertiseText>
                  Throughout my career, I've contributed to the 
                  architecture and development of flagship Turkish mobile applications,
                  delivering high-quality user experiences across multiple platforms.
                </ExpertiseText>
                
                <ProjectsList>
                  <ProjectItem 
                    variants={skillVariant}
                    initial="hidden"
                    animate={isVisible ? "visible" : "hidden"}
                  >
                    Iste Gelsin
                  </ProjectItem>
                  <ProjectItem 
                    variants={skillVariant}
                    initial="hidden"
                    animate={isVisible ? "visible" : "hidden"}
                  >
                    Cepte Sok
                  </ProjectItem>
                  <ProjectItem 
                    variants={skillVariant}
                    initial="hidden"
                    animate={isVisible ? "visible" : "hidden"}
                  >
                    Hopi
                  </ProjectItem>
                </ProjectsList>
                
                <ExpertiseText>
                  Currently, I balance my responsibilities as a consultant and entrepreneur,
                  collaborating with cross-functional teams to drive cutting-edge mobile 
                  solutions from concept to launch.
                </ExpertiseText>
              </ExpertiseContent>
            </ExpertiseSection>

            {/* Education */}
            <ExpertiseSection
              variants={fadeInUp}
              initial="hidden"
              animate={isVisible ? "visible" : "hidden"}
              transition={{ delay: 0.9 }}
            >
              <SectionTitle>Education &amp; Research</SectionTitle>
              <ExpertiseContent>
                <EducationItem>
                  <EducationIcon>
                    <GraduationCap size={20} />
                  </EducationIcon>
                  <EducationContent>
                    <EducationDegree>MSc in Computer Engineering</EducationDegree>
                    <EducationSchool>Politecnico di Milano</EducationSchool>
                  </EducationContent>
                </EducationItem>
                <EducationItem>
                  <EducationIcon>
                    <GraduationCap size={20} />
                  </EducationIcon>
                  <EducationContent>
                    <EducationDegree>BSc in Computer Engineering</EducationDegree>
                    <EducationSchool>Ege University</EducationSchool>
                  </EducationContent>
                </EducationItem>
                <ExpertiseText>
                  Through continuous research at Politecnico di Milano, I remain at the forefront 
                  of new technologies, ensuring that the solutions I develop are future-proof, 
                  scalable, and focused on delivering meaningful user experiences.
                </ExpertiseText>
              </ExpertiseContent>
            </ExpertiseSection>

            {/* Contact Section */}
            <ContactSection
              variants={fadeInUp}
              initial="hidden"
              animate={isVisible ? "visible" : "hidden"}
              transition={{ delay: 1.0 }}
            >
              <SectionTitle>Get in Touch</SectionTitle>
              <ExpertiseContent>
                <ExpertiseText>
                  Interested in discussing a potential project, collaboration, or simply want to 
                  learn more about my work? Feel free to reach out—I'm always excited to explore 
                  new ideas and opportunities.
                </ExpertiseText>
                <SocialLinks>
                  <SocialIcon
                    href="mailto:cem.sertkaya@cndsoftware.com"
                    onClick={(e) => {
                      e.preventDefault();
                      window.location.href = 'mailto:cem.sertkaya@cndsoftware.com';
                    }}
                    title="Send email"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Mail size={20} />
                  </SocialIcon>
                  <SocialIcon
                    href="https://linkedin.com/in/cemsertkaya"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="LinkedIn Profile"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Linkedin size={20} />
                  </SocialIcon>
                  <SocialIcon
                    href="https://github.com/cemsertkaya"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="GitHub Profile"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Github size={20} />
                  </SocialIcon>
                </SocialLinks>
              </ExpertiseContent>
            </ContactSection>
          </ProfileInfo>
        </ProfileSection>
      </MemberContainer>
    </PageContainer>
  );
};

export default CemSertkaya;